import type { Employee, EmployeeMonthlyInfo, MonthlyPayrollData, WorkSchedule } from '../../types/employee';
import {
  isBusinessServicePay,
  isDailyWorkerPay,
  resolvePayType,
} from '../../types/employee';
import type { RegulatoryRates } from '../../types/regulatoryRates';
import { useRatesStore } from '../../store/ratesStore';
import { ratesDefault2026 } from '../../constants/regulatoryRatesDefault';
import {
  calculateMonthlySalary,
  calculatePayrollDetail,
  calculateTotalWorkHours,
  getWeeklyHolidayAllowanceDetails,
} from '../../utils/payrollCalculations';
import {
  dailyWorkerForMonth,
  businessServiceForMonth,
} from './withholding-helper';
import {
  estimateDayInsurance,
  workerDeductionFromNetPay,
} from './daily-worker-insurance';
import {
  businessIncomeWithLocalFromRegulatory,
  dailyWorkerRatesFromRegulatory,
  dayInsuranceRatesFromRegulatory,
} from './types';
import { netPayFromMonthlyInfo } from './net-pay';

export function buildDailyWorkerMonthlyInfo(
  employee: Employee,
  schedulesInMonth: WorkSchedule[],
  rates: RegulatoryRates = useRatesStore.getState().current ?? ratesDefault2026,
): EmployeeMonthlyInfo {
  const workDays = schedulesInMonth.length;
  const dailyRate = Math.round(employee.dailyRate ?? 0);
  const baseSalary = workDays * dailyRate;
  const totalSalary = baseSalary;

  const dwRates = dailyWorkerRatesFromRegulatory(rates);
  const month = dailyWorkerForMonth(dailyRate, workDays, dwRates);

  const insuranceRates = dayInsuranceRatesFromRegulatory(rates);
  const estimate = estimateDayInsurance(employee, insuranceRates);
  const totalEmployeeInsurance = workerDeductionFromNetPay(
    employee,
    workDays,
    totalSalary,
    estimate,
    rates,
  );

  return {
    employee,
    totalWorkHours: 0,
    baseSalary,
    holidayAllowance: 0,
    totalSalary,
    hasInsurance: employee.hasFourInsurance || workDays > 0,
    totalEmployeeInsurance,
    withholdingTax: month.incomeTax,
    localTax: month.localTax,
    workDays,
  };
}

export function buildBusinessServiceMonthlyInfo(
  employee: Employee,
  schedulesInMonth: WorkSchedule[],
  rates: RegulatoryRates = useRatesStore.getState().current ?? ratesDefault2026,
): EmployeeMonthlyInfo {
  const workDays = schedulesInMonth.length;
  const unitRate = Math.round(employee.dailyRate ?? 0);
  const combinedRate = businessIncomeWithLocalFromRegulatory(rates);
  const result = businessServiceForMonth(unitRate, workDays, combinedRate);

  return {
    employee,
    totalWorkHours: 0,
    baseSalary: result.grossPay,
    holidayAllowance: 0,
    totalSalary: result.grossPay,
    hasInsurance: false,
    totalEmployeeInsurance: result.withholding,
    withholdingTax: 0,
    localTax: 0,
    workDays,
  };
}

function monthlyInfoToPayrollData(info: EmployeeMonthlyInfo): MonthlyPayrollData {
  const insuranceTotal = info.totalEmployeeInsurance;
  const withholding = isBusinessServicePay(info.employee)
    ? info.totalEmployeeInsurance
    : info.withholdingTax;
  const local = isBusinessServicePay(info.employee) ? 0 : info.localTax;

  return {
    employee: info.employee,
    totalWorkHours: info.totalWorkHours,
    baseSalary: info.baseSalary,
    holidayAllowance: info.holidayAllowance,
    totalSalary: info.totalSalary,
    insurance: {
      pension: 0,
      health: 0,
      longTermCare: 0,
      employment: isDailyWorkerPay(info.employee) ? insuranceTotal : 0,
      industrial: 0,
      total: insuranceTotal,
      breakdown: {
        pension: 0,
        health: 0,
        longTermCare: 0,
        employment: isDailyWorkerPay(info.employee) ? insuranceTotal : 0,
        industrial: 0,
      },
    },
    tax: {
      withholding,
      local,
      total: withholding + local,
    },
    netSalary: netPayFromMonthlyInfo(info),
    weeklyDetails: [],
    dependents: info.employee.dependents,
    laborDayAllowance: info.laborDayAllowance,
    laborDayIncluded: info.laborDayIncluded,
    workDays: info.workDays,
  };
}

export interface BuildMonthlyPayrollOptions {
  is5Plus?: boolean;
  getRateForDate?: (empId: string) => number;
  laborDayIncluded?: boolean;
  getLaborDayIncluded?: (empId: string) => boolean;
  laborDayIs5Plus?: boolean;
}

/** 정규 직원은 기존 `payrollCalculations` 위임 — diff 금지 */
export function buildMonthlyPayrollData(
  employee: Employee,
  schedulesInMonth: WorkSchedule[],
  selectedMonth: Date,
  options: BuildMonthlyPayrollOptions = {},
): MonthlyPayrollData {
  const payType = resolvePayType(employee.payType, employee.isHourly);

  if (payType === 'dailyWorker') {
    return monthlyInfoToPayrollData(buildDailyWorkerMonthlyInfo(employee, schedulesInMonth));
  }

  if (payType === 'businessService') {
    return monthlyInfoToPayrollData(buildBusinessServiceMonthlyInfo(employee, schedulesInMonth));
  }

  const salaryCalculation = calculateMonthlySalary(employee, schedulesInMonth, selectedMonth, {
    is5Plus: options.is5Plus,
    getRateForDate: options.getRateForDate,
    laborDayIncluded:
      options.getLaborDayIncluded?.(employee.id) ??
      options.laborDayIncluded ??
      true,
    laborDayIs5Plus: options.laborDayIs5Plus,
  });
  const totalHours = employee.isHourly ? calculateTotalWorkHours(schedulesInMonth) : 0;
  const weeklyDetails = getWeeklyHolidayAllowanceDetails(schedulesInMonth, employee, selectedMonth);
  const payrollDetail = calculatePayrollDetail(employee, salaryCalculation.totalSalary);

  return {
    employee,
    totalWorkHours: employee.isHourly ? Math.round(totalHours * 10) / 10 : 0,
    baseSalary: salaryCalculation.baseSalary,
    holidayAllowance: salaryCalculation.holidayAllowance,
    totalSalary: salaryCalculation.totalSalary,
    insurance: payrollDetail.insurance,
    tax: payrollDetail.tax,
    netSalary: payrollDetail.netSalary,
    weeklyDetails,
    dependents: employee.dependents,
    laborDayAllowance: salaryCalculation.laborDayAllowance,
    laborDayBreakdown: salaryCalculation.laborDayBreakdown,
    laborDayIncluded: salaryCalculation.laborDayIncluded,
    ...(salaryCalculation.regularPay !== undefined && { regularPay: salaryCalculation.regularPay }),
    ...(salaryCalculation.overtimePay !== undefined && { overtimePay: salaryCalculation.overtimePay }),
    ...(salaryCalculation.nightPay !== undefined && { nightPay: salaryCalculation.nightPay }),
    ...(salaryCalculation.holidayPay !== undefined && { holidayPay: salaryCalculation.holidayPay }),
  };
}

export function buildMonthlyInfoList(
  employees: Employee[],
  schedules: WorkSchedule[],
  monthStart: Date,
  monthEnd: Date,
  rates?: RegulatoryRates,
): EmployeeMonthlyInfo[] {
  const resolvedRates = rates ?? useRatesStore.getState().current ?? ratesDefault2026;

  return employees.map((employee) => {
    const payType = resolvePayType(employee.payType, employee.isHourly);
    const schedulesInMonth = schedules.filter((s) => {
      if (s.employee_id !== employee.id) return false;
      const d = new Date(s.date);
      return !isNaN(d.getTime()) && d >= monthStart && d <= monthEnd;
    });

    if (payType === 'dailyWorker') {
      return buildDailyWorkerMonthlyInfo(employee, schedulesInMonth, resolvedRates);
    }
    if (payType === 'businessService') {
      return buildBusinessServiceMonthlyInfo(employee, schedulesInMonth, resolvedRates);
    }

    const salaryCalculation = calculateMonthlySalary(employee, schedulesInMonth, monthStart);
    const payrollDetail = calculatePayrollDetail(employee, salaryCalculation.totalSalary);

    return {
      employee,
      totalWorkHours: employee.isHourly ? calculateTotalWorkHours(schedulesInMonth) : 0,
      baseSalary: salaryCalculation.baseSalary,
      holidayAllowance: salaryCalculation.holidayAllowance,
      totalSalary: salaryCalculation.totalSalary,
      hasInsurance: employee.hasFourInsurance,
      totalEmployeeInsurance: payrollDetail.insurance.total,
      withholdingTax: payrollDetail.tax.withholding,
      localTax: payrollDetail.tax.local,
    };
  });
}
